import React, { useContext, useState } from "react";
import { connect } from "react-redux";
import { Box, Flex } from "@acko-tech/building-blocks.ui.common";

import { Context } from "@components/useContext/context";

import {
  ItemsContainer,
  Item,
  ItemName,
  ItemDescription,
  Option,
  OptionName,
  ZdTable,
  ZdItem,
  Row,
  ModalPrimaryContent,
  ModalSecondaryContent,
  ZdExampleCard
} from "./styles";

import ImageContainer from "@components/imageContainer";
import HorinzontalScroll from "@components/horizontalScroll";
import Banner, { IBanner } from "@components/banner";
import { CommonProps } from "@utils/interface";
import DOMPurify from "isomorphic-dompurify";
import { resolvePath } from "@utils/resolvePath";
import {
  scrollIntoViewWrapper,
  getWidgetSpecificIsVisible
} from "@utils/index";
import Modal from "@components/modal";

interface ICoveredNotCoveredAddons extends CommonProps {
  addonId?: string;
  options: {
    bannerData?: IBanner;
    optionId: string;
    optionName: string;
    iterableData: {
      display_name: string;
      description: string;
      image: string;
      [key: string]: any;
    }[];
  }[];
  coverageDetails: string;
  heading: string;
  modalHeadingStyles?: React.CSSProperties;
}

const zeroDepCover = [
  {
    feature_table_title: "Depreciation % <br />  on various parts",
    with_zd_table_titile: "With <br /> Zero Dep.",
    without_zd_table_titile: "Without <br /> Zero Dep."
  },
  {
    feature: "For all plastic, rubber, nylon parts and batteries",
    with: "0%",
    without: "50%"
  },
  {
    feature: "For fibre components",
    with: "0%",
    without: "30%"
  },
  {
    feature: "For glass components",
    with: "0%",
    without: "0%"
  },
  {
    feature: "For metal, wooden and all other parts",
    with: "0%",
    without: "25%"
  },
  {
    feature: "For paint",
    with: "0%",
    without: "50%"
  }
];

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    )
  };
};

const CoveredNotCoveredAddons = (props: ICoveredNotCoveredAddons) => {
  const { isDesktop } = useContext(Context);

  const {
    addonId,
    options: initialOptions,
    is_visible,
    actions,
    coverageDetails,
    heading
  } = props;

  const { on_close, on_click } = actions || {};

  let options = initialOptions;

  if (!addonId?.includes("zero_dep")) {
    options = initialOptions.filter((o) => o.optionId !== "zd_details");
  }

  const [selectedOption, setSelectedOption] = useState(options[0]);

  const { text, image } = selectedOption?.bannerData || {};
  const { imgSrc, height, width, alt } = image || {};

  return (
    <Modal
      showModal={is_visible}
      onClose={() => {
        if (on_close?.length > 0) {
          on_close?.forEach((eachFunc: any) => {
            typeof eachFunc.funCall === "function" &&
              eachFunc.funCall(
                //@ts-ignore
                {
                  ...eachFunc.args
                } || {}
              );
          });
        }
      }}
      cueText={coverageDetails}
      cueTextStyles={{
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "18px"
      }}
      dWebCueTextStyles={{
        marginBottom: "1.5rem"
      }}
      modalHeading={heading}
      modalHeadingStyles={{
        color: "rgb(69, 25, 153)",
        ...(!isDesktop && { maxWidth: "80%" }),
        padding: isDesktop ? "2rem 2rem 0px" : "2rem 1.25rem 0px",
        ...(props.modalHeadingStyles || {})
      }}
      showModalFooter={true}
      showPrimaryCta={true}
      primaryCtaText="OK"
      primaryCtaClickHandler={() => {
        if (on_click?.length > 0) {
          on_click?.forEach((eachFunc: any) => {
            typeof eachFunc.funCall === "function" &&
              eachFunc.funCall(
                //@ts-ignore
                {
                  ...eachFunc.args
                } || {}
              );
          });
        }
      }}
      wrapperStyles={{ padding: "0rem" }}
      styles={{ padding: "0rem" }}
      footerWrapperStyles={{ padding: "1.25rem" }}
      footerWrapperDStyles={{ padding: "2rem", marginTop: "0px" }}
      closeButtonStyles={{
        ...(!isDesktop && { right: "1.25rem" }),
        top: "2rem",
        right: isDesktop ? "2rem" : "1.25rem"
      }}
    >
      <div style={{ padding: "0px 2px" }}>
        <HorinzontalScroll
          styles={{ marginLeft: isDesktop ? "2rem" : "1.25rem" }}
        >
          {Object.keys(options).map((o) => {
            const currentOption = options[o];
            const isAddon = currentOption.optionId === "addons";
            const hasIterableData = currentOption.iterableData?.length > 0;

            if (isAddon && !hasIterableData) return null;

            return (
              currentOption.optionName && (
                <Option
                  isSelected={
                    currentOption.optionId === selectedOption?.optionId
                  }
                  isUpgradeOption={isAddon}
                  onClick={() => {
                    scrollIntoViewWrapper(currentOption.optionId);
                    setSelectedOption(currentOption);
                  }}
                  id={currentOption.optionId}
                >
                  <OptionName
                    isSelected={
                      currentOption.optionId === selectedOption?.optionId
                    }
                    isUpgradeOption={isAddon}
                  >
                    {currentOption.optionName}
                  </OptionName>
                </Option>
              )
            );
          })}
        </HorinzontalScroll>
      </div>

      <div style={{ padding: isDesktop ? "0px 2rem" : "0px 1.25rem" }}>
        {selectedOption?.bannerData && (
          <>
            <Banner
              image={{ imgSrc: imgSrc, height, width, alt }}
              text={text}
              styles={{
                marginTop: "1.5rem",
                alignItems: "center"
              }}
              imgAlt="banner"
            />
          </>
        )}

        <ItemsContainer>
          {selectedOption?.iterableData?.map((o) => {
            return (
              <Item>
                <ImageContainer
                  imgSrc={o.image}
                  alt="icon"
                  width={45}
                  height={45}
                />{" "}
                <Flex flexDirection="column" style={{ gap: "4px" }}>
                  <ItemName>{o.display_name}</ItemName>
                  <ItemDescription
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(o?.description)
                    }}
                  />
                </Flex>
              </Item>
            );
          })}
        </ItemsContainer>

        {selectedOption?.optionId === "zd_details" && (
          <>
            <ModalSecondaryContent styles={{ marginBottom: "1rem" }}>
              Check the cost of car parts you'll have to pay with and without a
              Zero Depreciation Plan below.
            </ModalSecondaryContent>
            <Flex style={{ justifyContent: "center", alignItems: "center" }}>
              <ZdTable>
                {zeroDepCover.map((zdItem, itemIdx) => {
                  return (
                    <Row>
                      {Object.values(zdItem).map((value, idx) => (
                        <>
                          <ZdItem
                            background={idx === 1 && "#F3FAF7"}
                            isTitle={itemIdx === 0}
                            isFeature={idx === 0}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(value)
                            }}
                          />
                        </>
                      ))}
                    </Row>
                  );
                })}
              </ZdTable>
            </Flex>
            <ModalPrimaryContent style={{ margin: "1rem 0px" }}>
              Here is an example,
            </ModalPrimaryContent>
            <ModalSecondaryContent>
              Let's say your car meets with an accident and the front bumper
              which is made of plastic gets completely damaged
            </ModalSecondaryContent>

            <ZdExampleCard>
              <Box
                style={{
                  padding: "1rem",
                  backgroundColor: "#F8F7FC",
                  width: "auto"
                }}
              >
                <ModalPrimaryContent
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px"
                  }}
                >
                  Repair cost: ₹10,000
                </ModalPrimaryContent>
                <ModalSecondaryContent
                  style={{
                    marginTop: "4px",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "18px"
                  }}
                >
                  Depreciation cost on <b>plastic materials is flat 50%</b>
                </ModalSecondaryContent>
              </Box>

              <Flex
                flexDirection="row"
                style={{
                  gap: "0.5rem",
                  alignItems: "start",
                  padding: "1rem",
                  borderBottom: "1px solid #E7E7F0",
                  width: "auto"
                }}
              >
                <div style={{ minWidth: "22px" }}>
                  <ImageContainer
                    imgSrc={resolvePath("/images/icons/green-tick.svg")}
                    width={22}
                    height={22}
                    alt={"tick"}
                  />
                </div>
                <div>
                  <ModalPrimaryContent
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "20px"
                    }}
                  >
                    With Zero Depreciation
                  </ModalPrimaryContent>
                  <ModalSecondaryContent
                    style={{
                      marginTop: "4px",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "18px"
                    }}
                  >
                    You don't have to pay anything. ACKO will pay ₹10,000 for
                    the repairs.
                  </ModalSecondaryContent>
                </div>
              </Flex>

              <Flex
                flexDirection="row"
                style={{
                  gap: "0.5rem",
                  alignItems: "start",
                  padding: "1rem",
                  width: "auto"
                }}
              >
                <div style={{ minWidth: "22px" }}>
                  <ImageContainer
                    imgSrc={resolvePath("/images/icons/warning.svg")}
                    width={22}
                    height={22}
                    alt={"tick"}
                  />
                </div>
                <div>
                  <ModalPrimaryContent
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "20px"
                    }}
                  >
                    Without Zero Depreciation
                  </ModalPrimaryContent>
                  <ModalSecondaryContent
                    style={{
                      marginTop: "4px",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "18px"
                    }}
                  >
                    <b>ACKO will pay ₹5,000</b> (50%) and you will have to pay
                    the remaining amount.
                  </ModalSecondaryContent>
                </div>
              </Flex>
            </ZdExampleCard>
          </>
        )}
      </div>
    </Modal>
  );
};

export default connect(mapStateToProps)(CoveredNotCoveredAddons);
